import { useTranslations } from '@vocab/react';
import { Stack, Text } from 'braid-design-system';
import { useCallback, useEffect, useState } from 'react';

import { useHotjarEvent } from 'src/hooks/useHotjarEvent';

import translations from '../.vocab';
import RefreshRecommendationsButton from '../RefreshRecommendationsButton';
import {
  useGetRefreshRecommendationsWithCandidateChatContext,
  useGetResetCandidateChatContext,
} from '../hooks/candidateChatContextHelpers';
import { useCareerFeedAIAssistant } from '../hooks/useCareerFeedAIAssistant';

import { CareerFeedAIAssistantContentFrame } from './CareerFeedAIAssistantContentFrame/CareerFeedAIAssistantContentFrame';
import { CareerFeedAIAssistantDrawer } from './CareerFeedAIAssistantDrawer/CareerFeedAIAssistantDrawer';

// This hook is responsible for refreshing the recommendations when the user refines their preferences.
export const useRefreshRecsWhenRefined = () => {
  const {
    tracking: { trackRecommendedJobsRefreshed, trackGenAIChatEnded },
    progress,
  } = useCareerFeedAIAssistant();

  const refreshRecommendations =
    useGetRefreshRecommendationsWithCandidateChatContext();

  const refreshRecs = useCallback(async () => {
    const result = await refreshRecommendations();
    if (result.success) {
      trackGenAIChatEnded();
      trackRecommendedJobsRefreshed({ jobsCount: result.jobsCount });
    } else {
      trackGenAIChatEnded(Boolean(result.error));
      trackRecommendedJobsRefreshed({ jobsCount: -1 });
    }
  }, [
    refreshRecommendations,
    trackGenAIChatEnded,
    trackRecommendedJobsRefreshed,
  ]);

  const [isRefreshedRecs, setIsRefreshedRecs] = useState(false);

  useEffect(() => {
    if (progress === 'recsRefined' && !isRefreshedRecs) {
      setIsRefreshedRecs(true);
      refreshRecs();
    }

    return () => {
      if (progress !== 'recsRefined') {
        setIsRefreshedRecs(false);
      }
    };
  }, [isRefreshedRecs, progress, refreshRecs]);
};

export const ConversationFinished = () => {
  const resetPreferences = useGetResetCandidateChatContext();
  const {
    lastCompletedAssistantResponse,
    tracking: { trackGenAIChatResetPressed },
  } = useCareerFeedAIAssistant();
  const { t } = useTranslations(translations);

  useHotjarEvent('homepage_genAI_end');

  const onReset = useCallback(() => {
    trackGenAIChatResetPressed('chat widget');
    resetPreferences();
  }, [resetPreferences, trackGenAIChatResetPressed]);

  const message = lastCompletedAssistantResponse?.text ?? '';

  return (
    <>
      <CareerFeedAIAssistantContentFrame
        headMessage={message}
        contentSection={<></>}
        ctaSection={
          <Stack space="small">
            <RefreshRecommendationsButton onReset={onReset} />
            <Text size="xsmall" tone="secondary">
              {t('These AI-powered job suggestions....')}
            </Text>
          </Stack>
        }
        isLoading={
          false /* Loading is just used for loading overlay, we don't want this on the final q */
        }
      />
      <CareerFeedAIAssistantDrawer
        isLoading={
          false
        } /* Loading is just used for loading overlay, we don't want this on the final q */
      />
    </>
  );
};
